import { createContext, useContext, useEffect, useMemo, useState, useCallback, type ReactNode } from "react";
import { ToolStateMachine } from "../../../application/map/tools/toolStateMachine";
import type { ToolId, ToolState, MapTool } from "../../../application/map/tools/types";

interface MapToolContextValue {
	state: ToolState;
	activate: (id: ToolId) => void;
	deactivate: () => void;
	toggle: (id: ToolId) => void;
	register: (tool: MapTool) => void;
	getRegistered: (id: ToolId) => MapTool | null;
	cursor: string | undefined;
}

const MapToolCtx = createContext<MapToolContextValue | null>(null);

export function MapToolProvider({ children }: { children: ReactNode }) {
	const machine = useMemo(() => new ToolStateMachine(), []);
	const [state, setState] = useState<ToolState>(machine.state);

	useEffect(() => machine.onChange(setState), [machine]);

	const activate = useCallback((id: ToolId) => machine.activate(id), [machine]);
	const deactivate = useCallback(() => machine.deactivate(), [machine]);
	const toggle = useCallback((id: ToolId) => machine.toggle(id), [machine]);
	const register = useCallback((tool: MapTool) => machine.register(tool), [machine]);
	const getRegistered = useCallback((id: ToolId) => machine.getRegistered(id), [machine]);

	const value = useMemo<MapToolContextValue>(
		() => ({
			state,
			activate,
			deactivate,
			toggle,
			register,
			getRegistered,
			cursor: machine.cursor,
		}),
		[state, activate, deactivate, toggle, register, getRegistered, machine],
	);

	return <MapToolCtx.Provider value={value}>{children}</MapToolCtx.Provider>;
}

export function useMapTool(): MapToolContextValue {
	const ctx = useContext(MapToolCtx);
	if (!ctx) throw new Error("useMapTool must be used inside MapToolProvider");
	return ctx;
}
